import { useCallback, useMemo, useState } from "react"
import {
	IconArrowUpDownTrailFill18 as ArrowDownUp,
	IconOffice3Fill18 as Building2,
	IconCircleDashedFill18 as CircleDot,
	IconMapFill18 as MapIcon,
	IconMessageBubbleUserFill18 as SourceIcon,
	IconTag2Fill18 as TagIcon,
	IconUsersFill18 as Users,
} from "nucleo-ui-fill-18"

import type { NestedCommandGroup } from "@productlane/ui/src/components/internal/nested-command"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { FilterType } from "./filter-popover-items/types"
import { FilterPopover } from "@/components/filters"
import { CompanyFilterItems } from "./filter-popover-items/company-filter-items"
import { LabelFilterItems } from "./filter-popover-items/label-filter-items"
import { PriorityFilterItems } from "./filter-popover-items/priority-filter-items"
import { ProjectFilterItems } from "./filter-popover-items/project-filter-items"
import { SourceFilterItems } from "./filter-popover-items/source-filter-items"
import { StatusFilterItems } from "./filter-popover-items/status-filter-items"
import { TeamFilterItems } from "./filter-popover-items/team-filter-items"
import { useRoadmapFilterState } from "./roadmap-filter-state"
import {
	useFilteredRoadmapSearchItems,
	useRoadmapSearchableItems,
} from "./use-roadmap-searchable-items"

export const FILTER_CATEGORIES: NestedCommandGroup<FilterType>[] = [
	{ type: "Statuses", label: "Status", Icon: CircleDot },
	{ type: "Labels", label: "Label", Icon: TagIcon },
	{ type: "Teams", label: "Team", Icon: Users },
	{ type: "Projects", label: "Project", Icon: MapIcon },
	{ type: "Priorities", label: "Priority", Icon: ArrowDownUp },
	{ type: "Companies", label: "Company", Icon: Building2 },
	{ type: "Sources", label: "Source", Icon: SourceIcon },
]

const CATEGORIES_WITH_CUSTOM_FILTER: FilterType[] = [
	"Projects",
	"Teams",
	"Companies",
]

function FilterSubPanel({
	activeFilter,
	query,
	setQuery,
	onFilterItemSelect,
}: {
	activeFilter: FilterType
	query: string
	setQuery: (q: string) => void
	onFilterItemSelect: () => void
}) {
	const hasCustomFilter = CATEGORIES_WITH_CUSTOM_FILTER.includes(activeFilter)

	return (
		<Command shouldFilter={!hasCustomFilter}>
			<CommandInput
				key={activeFilter}
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[min(70dvh,320px)]">
				<CommandEmpty>No results found.</CommandEmpty>
				<CommandGroup>
					{activeFilter === "Statuses" && (
						<StatusFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Teams" && (
						<TeamFilterItems query={query} onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Projects" && (
						<ProjectFilterItems query={query} onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Priorities" && (
						<PriorityFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Companies" && (
						<CompanyFilterItems query={query} onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Sources" && (
						<SourceFilterItems onSelect={onFilterItemSelect} />
					)}
					{activeFilter === "Labels" && (
						<LabelFilterItems onSelect={onFilterItemSelect} />
					)}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

export function RoadmapFilter() {
	const [globalQuery, setGlobalQuery] = useState("")
	const filterState = useRoadmapFilterState()

	const counts = useMemo(
		() =>
			({
				Statuses: filterState.statuses.length,
				Teams: filterState.teamIds.length,
				Projects: filterState.projectIds.length,
				Priorities: filterState.priorities.length,
				Companies: filterState.companyIds.length,
				Sources: filterState.sources.length,
				Labels: filterState.labelIds.length,
			}) as Record<FilterType, number>,
		[filterState],
	)

	const onCategorySelect = useCallback(() => {
		setGlobalQuery("")
	}, [])

	const searchableItems = useRoadmapSearchableItems({
		onCategorySelect: onCategorySelect as () => void,
		query: globalQuery,
	})
	const filteredSearchItems = useFilteredRoadmapSearchItems(
		globalQuery,
		searchableItems,
	)

	const renderSubPanel = useCallback(
		(
			group: FilterType,
			query: string,
			setQuery: (q: string) => void,
			onFilterItemSelect: () => void,
		) => (
			<FilterSubPanel
				activeFilter={group}
				query={query}
				setQuery={setQuery}
				onFilterItemSelect={onFilterItemSelect}
			/>
		),
		[],
	)

	return (
		<FilterPopover<FilterType>
			categories={FILTER_CATEGORIES}
			counts={counts}
			renderSubPanel={renderSubPanel}
			searchableItems={searchableItems}
			filteredSearchItems={filteredSearchItems}
			onGlobalQueryChange={setGlobalQuery}
			closeOnSelect
		/>
	)
}
